import { useEffect, useRef, useState } from '@wordpress/element';

export function useAirPlay(audioRef, currentTrackId) {
    const [airPlayAvailable, setAirPlayAvailable] = useState(false);
    const [airPlayConnected, setAirPlayConnected] = useState(false);
    const [airPlayIsPlaying, setAirPlayIsPlaying] = useState(false);

    const airPlayConnectedRef = useRef(false);

    function setConnected(value) {
        airPlayConnectedRef.current = value;
        setAirPlayConnected(value);
    }

    useEffect(() => {
        const audio = audioRef.current;

        if (!audio) return;

        function handleAvailability(event) {
            setAirPlayAvailable(event.availability === 'available');
        }

        function handleRouteChange() {
            const isRemote =
                audio.webkitCurrentPlaybackTargetIsWireless === true;

            setConnected(isRemote);
            setAirPlayIsPlaying(isRemote && !audio.paused);
        }

        function handlePlay() {
            if (airPlayConnectedRef.current) {
                setAirPlayIsPlaying(true);
            }
        }

        function handlePause() {
            if (airPlayConnectedRef.current) {
                setAirPlayIsPlaying(false);
            }
        }

        audio.addEventListener(
            'webkitplaybacktargetavailabilitychanged',
            handleAvailability
        );

        audio.addEventListener(
            'webkitcurrentplaybacktargetiswirelesschanged',
            handleRouteChange
        );

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handlePause);

        if (audio.webkitShowPlaybackTargetPicker) {
            setAirPlayAvailable(true);
        }

        handleRouteChange();

        return () => {
            audio.removeEventListener(
                'webkitplaybacktargetavailabilitychanged',
                handleAvailability
            );

            audio.removeEventListener(
                'webkitcurrentplaybacktargetiswirelesschanged',
                handleRouteChange
            );

            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handlePause);
        };
    }, [audioRef, currentTrackId]);

    async function openAirPlayPicker() {
        const audio = audioRef.current;

        if (!audio?.webkitShowPlaybackTargetPicker) {
            return;
        }

        try {
            if (audio.paused) {
                await audio.play().catch(() => { });
            }

            audio.webkitShowPlaybackTargetPicker();
        } catch (error) {
            console.error('Failed to open AirPlay picker:', error);
        }
    }

    return {
        airPlayAvailable,
        airPlayConnected,
        airPlayIsPlaying,
        openAirPlayPicker,
    };
}